import type { RoleEnum } from '@zeus/index'
import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader, MailCheck, Moon, Sun } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { BrandLogo } from '~/components/BrandLogo'
import { Button } from '~/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { useTheme } from '~/components/ui/hooks/use-theme'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_LOGIN, GQL_REQUEST_MAGIC_LINK } from '~/gql/auth/auth'
import { useBranding } from '~/helpers/branding/branding'
import { useAuth } from '~/helpers/store/auth'
import { getDefaultRoute } from '~/routes/router'
import LoginSSO from './LoginSSO'

/**
 * L'écran accueille deux entrées (#39) : mot de passe et magic link. Le
 * formulaire bascule d'un mode à l'autre plutôt que d'empiler deux champs
 * email, et `sentTo` mémorise l'adresse pour l'écran de confirmation.
 */
type LoginMode = 'password' | 'magic'

/**
 * Codes d'erreur que le callback SSO (#40) peut renvoyer dans l'URL. Un code
 * inconnu retombe sur un message générique : la liste sert à expliquer les
 * refus courants, pas à énumérer tous les échecs possibles.
 */
const SSO_ERROR_KEYS: Record<string, string> = {
  SSO_ERROR_DOMAIN_NOT_ALLOWED: 'sso.error.domainNotAllowed',
  SSO_ERROR_ACCOUNT_DISABLED: 'sso.error.accountDisabled',
  SSO_ERROR_EMAIL_ALREADY_USED: 'sso.error.emailAlreadyUsed',
  SSO_ERROR_PROVIDER_UNAVAILABLE: 'sso.error.providerUnavailable',
}

function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { theme, setTheme } = useTheme()
  const { t, i18n } = useTranslation()
  const { setPayload, setLoading } = useAuth()
  const { signupCtaVisible } = useBranding()

  const loginSchema = z.object({
    email: z.string().email({ message: t('input.email.message') }),
    password: z.string().min(1, { message: t('input.password') }),
  })

  const magicSchema = z.object({
    email: z.string().email({ message: t('input.email.message') }),
  })

  type LoginFormValues = z.infer<typeof loginSchema>
  type MagicFormValues = z.infer<typeof magicSchema>

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const magicForm = useForm<MagicFormValues>({
    resolver: zodResolver(magicSchema),
    defaultValues: { email: '' },
  })

  const [loginGql, { loading }] = useMutation(GQL_LOGIN)
  const [requestMagicLink, { loading: magicLoading }] = useMutation(GQL_REQUEST_MAGIC_LINK)
  const [authFailed, setAuthFailed] = useState(false)
  const [mode, setMode] = useState<LoginMode>('password')
  const [sentTo, setSentTo] = useState<string | null>(null)

  const onSubmit = async (values: LoginFormValues) => {
    setAuthFailed(false)
    try {
      const { data } = await loginGql({ variables: { email: values.email, password: values.password } })
      if (data?.login.__typename === 'UserInfo') {
        setPayload({
          firstName: data.login.firstName,
          lastName: data.login.lastName,
          email: data.login.email,
          role: data.login.roles,
          id: data.login.id,
        })
        setLoading(false)
        navigate(getDefaultRoute(data.login.roles as RoleEnum))
      } else {
        // CustomError (identifiants incorrects) — pas une exception (#97).
        setAuthFailed(true)
      }
    } catch {
      toast.error(t('bad.server-login.message'))
    }
  }

  const onMagicSubmit = async (values: MagicFormValues) => {
    try {
      await requestMagicLink({ variables: { email: values.email } })
      // L'API répond `true` même pour une adresse inconnue (anti-énumération) :
      // l'écran de confirmation reste donc volontairement au conditionnel.
      setSentTo(values.email)
    } catch {
      toast.error(t('bad.server-login.message'))
    }
  }

  /** Bascule de mode en conservant l'email déjà saisi. */
  const switchMode = (next: LoginMode) => {
    setAuthFailed(false)
    setSentTo(null)
    if (next === 'magic') magicForm.setValue('email', form.getValues('email'))
    else form.setValue('email', magicForm.getValues('email'))
    setMode(next)
  }

  const ssoError = searchParams.get('error')
  const ssoErrorMessage = ssoError
    ? t(SSO_ERROR_KEYS[ssoError] ?? 'sso.error.generic')
    : null

  const isFr = i18n.language.startsWith('fr')
  const toggleLang = () => i18n.changeLanguage(isFr ? 'en' : 'fr')

  return (
    <div className="flex h-dvh w-full items-center justify-center overflow-hidden bg-muted/40 p-0 sm:p-12">
      <div className="grid h-full w-full max-w-5xl overflow-hidden bg-card shadow-xl sm:max-h-[820px] sm:rounded-3xl md:grid-cols-[47fr_53fr]">
        {/* Panneau visuel : scène claire chaleureuse + illustration */}
        <aside className="relative m-3 hidden overflow-hidden rounded-2xl bg-gradient-to-br from-orange-50 to-rose-100 p-8 md:flex md:flex-col">
          {/* Halo à la couleur de marque de l'org */}
          <div className="pointer-events-none absolute -right-16 -top-16 size-56 rounded-full bg-primary/15 blur-3xl" />

          <div className="relative z-10">
            <h2 className="whitespace-pre-line font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-balance text-neutral-900">
              {t('login.tagline')}
            </h2>
            <p className="mt-4 max-w-[30ch] leading-relaxed text-neutral-600">
              {t('login.tagline.sub')}
            </p>
          </div>

          {/* L'illustration (PNG détouré) occupe l'espace restant et se réduit
              sur les petits écrans. */}
          <div className="relative z-10 mt-4 flex min-h-0 flex-1 items-end justify-center">
            <img
              src="/login-hero.png"
              alt=""
              aria-hidden="true"
              className="max-h-full w-auto max-w-full object-contain"
            />
          </div>
        </aside>

        {/* Panneau formulaire */}
        <main className="relative flex flex-col overflow-y-auto p-6 sm:p-10 lg:p-14">
          <div className="mb-8 flex items-center justify-between gap-4">
            <BrandLogo className="h-8 w-auto" fallbackClassName="font-heading text-lg font-bold" />
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleLang}
                className="rounded-full font-medium"
                aria-label={isFr ? t('common.english') : t('common.french')}
              >
                {isFr ? 'FR' : 'EN'}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                className="rounded-full"
                aria-label={theme === 'light' ? t('theme.dark') : t('theme.light')}
              >
                {theme === 'light' ? <Moon className="size-5" /> : <Sun className="size-5" />}
              </Button>
            </div>
          </div>

          <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
            <h1 className="font-heading text-4xl font-extrabold tracking-tight">{t('common.login')}</h1>
            <p className="mt-2 mb-8 text-muted-foreground">
              {mode === 'password' ? t('common.login.description') : t('login.magic-link.description')}
            </p>

            {ssoErrorMessage && (
              <p role="alert" className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {ssoErrorMessage}
              </p>
            )}

            {mode === 'magic' && sentTo && (
              <div className="flex flex-col">
                <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-muted/50 px-6 py-8 text-center">
                  <MailCheck className="size-8 text-primary" />
                  <p className="font-heading text-lg font-semibold">{t('login.magic-link.sent.title')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('login.magic-link.sent.description', { email: sentTo })}
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  disabled={magicLoading}
                  onClick={() => onMagicSubmit({ email: sentTo })}
                  className="mt-6 h-12 w-full rounded-full"
                >
                  {magicLoading && <Loader className="mr-2 size-4 animate-spin" />}
                  {t('login.magic-link.resend')}
                </Button>

                <button
                  type="button"
                  onClick={() => switchMode('password')}
                  className="mt-6 text-center text-sm font-semibold text-primary hover:underline hover:underline-offset-4"
                >
                  {t('login.password.cta')}
                </button>
              </div>
            )}

            {mode === 'magic' && !sentTo && (
              <Form {...magicForm}>
                <form onSubmit={magicForm.handleSubmit(onMagicSubmit)} className="flex flex-col">
                  <FormField
                    control={magicForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="mb-6">
                        <div className="grid gap-2">
                          <Label htmlFor="magic-email" className="text-xs font-semibold text-muted-foreground">{t('common.email')}</Label>
                          <FormControl>
                            <Input
                              id="magic-email"
                              type="email"
                              placeholder="vous@exemple.com"
                              className="h-12 rounded-xl bg-muted"
                              {...field}
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" disabled={magicLoading} className="h-14 w-full rounded-full text-base font-semibold">
                    {magicLoading && <Loader className="mr-2 size-4 animate-spin" />}
                    {t('login.magic-link.submit')}
                  </Button>

                  <button
                    type="button"
                    onClick={() => switchMode('password')}
                    className="mt-6 text-center text-sm font-semibold text-primary hover:underline hover:underline-offset-4"
                  >
                    {t('login.password.cta')}
                  </button>
                </form>
              </Form>
            )}

            {mode === 'password' && (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="mb-4">
                        <div className="grid gap-2">
                          <Label htmlFor="email" className="text-xs font-semibold text-muted-foreground">{t('common.email')}</Label>
                          <FormControl>
                            <Input
                              id="email"
                              type="email"
                              placeholder="vous@exemple.com"
                              className="h-12 rounded-xl bg-muted"
                              {...field}
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem className="mb-2">
                        <div className="grid gap-2">
                          <Label htmlFor="password" className="text-xs font-semibold text-muted-foreground">{t('common.password')}</Label>
                          <FormControl>
                            <Input
                              id="password"
                              type="password"
                              placeholder="••••••••"
                              className="h-12 rounded-xl bg-muted"
                              {...field}
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <button
                    type="button"
                    onClick={() => navigate('/reset-password')}
                    className="mb-6 ml-auto text-sm font-semibold text-primary hover:underline hover:underline-offset-4"
                  >
                    {t('password.forgot')}
                  </button>

                  {authFailed && (
                    <p role="alert" className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                      {t('bad.connection.message')}
                    </p>
                  )}

                  <Button type="submit" disabled={loading} className="h-14 w-full rounded-full text-base font-semibold">
                    {loading && <Loader className="mr-2 size-4 animate-spin" />}
                    {t('common.login')}
                  </Button>

                  <div className="my-6 flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="h-px flex-1 bg-border" />
                    {t('common.or')}
                    <span className="h-px flex-1 bg-border" />
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => switchMode('magic')}
                    className="mb-3 h-12 w-full rounded-full"
                  >
                    {t('login.magic-link.cta')}
                  </Button>

                  <LoginSSO />

                  {/*
                    Accroche d'acquisition du parcours d'essai (#155) : elle ne
                    s'affiche que là où la plateforme a le droit de se montrer,
                    c'est-à-dire sur le gratuit (#216). Sur l'espace d'un client,
                    ses apprenants ne sont pas démarchés depuis sa page de
                    connexion.
                  */}
                  {signupCtaVisible && (
                    <p className="mt-6 text-center text-sm text-muted-foreground">
                      {t('signup.prompt')}
                      {' '}
                      <button
                        type="button"
                        onClick={() => navigate('/signup')}
                        className="font-semibold text-primary hover:underline hover:underline-offset-4"
                      >
                        {t('signup.cta')}
                      </button>
                    </p>
                  )}
                </form>
              </Form>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Login
